<script lang="ts">
    import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
    import LoadMoreButton from "@apps/iloom_customization/components/LoadMoreButton.svelte";
    import Page from "@apps/iloom_customization/components/Page.svelte";
    import PageTitle from "@apps/iloom_customization/components/PageTitle.svelte";
    import Pagination from "@apps/iloom_customization/components/Pagination.svelte";
    import Sort from "@apps/iloom_customization/components/Sort.svelte";
    import { ProductQuery } from "@apps/iloom_customization/modules/products/queries/ProductQuery";
    import ReviewsFeaturedList from "@apps/iloom_customization/reviews/components/ReviewsFeaturedList.svelte";
    import ReviewsList from "@apps/iloom_customization/reviews/components/ReviewsList.svelte";
    import ReviewsPageSizeTabs from "@apps/iloom_customization/reviews/components/ReviewsPageSizeTabs.svelte";
    import ReviewsPageSpaceTabs from "@apps/iloom_customization/reviews/components/ReviewsPageSpaceTabs.svelte";
    import { IloomAttributesFiltersQuery } from "@apps/iloom_customization/reviews/queries/IloomAttributesFiltersQuery";
    import { ReviewsQuery } from "@apps/iloom_customization/reviews/queries/ReviewsQuery";
    import { IloomReviewsService } from "@apps/iloom_customization/reviews/services/IloomReviewsService";
    import { REVIEWS_LIMIT } from "@apps/iloom_customization/reviews/stores/ReviewsStore";
    import { WishlistService } from "@apps/iloom_customization/wishlist/services/WishlistService";
    import ReviewEntity from "@apps/reviews/entities/ReviewEntity";
    import { GetReviewsWithFiltersParamsDTO } from "@apps/reviews/services/ReviewsServiceBase";
    import ReviewsGalleryManager from "@apps/reviews/widgets/reviews_gallery_widget/ReviewsGalleryManager";
    import { serializeFilters } from "@apps/reviews/widgets/reviews_list_widget/helpers/serializeFilters";
    import { WishlistProductStatusesQuery } from "@apps/wishlist/queries/WishlistProductStatusesQuery";
    import { WishlistsService } from "@apps/wishlist/services/WishlistsService";
    import {
        BehaviorSubject,
        Observable,
        combineLatest,
        lastValueFrom,
    } from "rxjs";
    import { onMount } from "svelte";
    import { container } from "tsyringe";

    import TabsManager from "@components/Tabs/TabsManager";

    export let productQuery: ProductQuery;
    export let iloomAttributesFiltersQuery: IloomAttributesFiltersQuery;
    export let reviewsQuery: ReviewsQuery;
    export let reviewsService: IloomReviewsService;
    export let sizeTabsManager: TabsManager;
    export let spaceTabsManager: TabsManager;
    export let wishlistService: WishlistService;
    export let wishlistsService: WishlistsService;
    export let wishlistProductStatusesQuery: WishlistProductStatusesQuery;

    const isMobile = window.innerWidth <= 600;
    const reviewsGalleryManager = container.resolve(ReviewsGalleryManager);

    const meta$ = reviewsQuery.meta$;
    const loadMoreStatus$ = reviewsQuery.loadMoreStatus$;

    const attributesFilters$ = iloomAttributesFiltersQuery.attributesFilters$;

    const sortTabs = [
        {
            value: "mostRecent",
            name: "最新的",
        },
        {
            value: "mostHelpful",
            name: "熱門排序",
        },
    ];

    type SortType = "mostRecent" | "mostHelpful";

    const openedSpaceTab$ = spaceTabsManager.openedTab$;
    const openedSizeTab$ = sizeTabsManager.openedTab$;
    const page$ = new BehaviorSubject<number>(1);
    const currentSort$ = new BehaviorSubject<SortType>("mostRecent");

    const handleChangePage = (event: CustomEvent) => page$.next(event.detail);
    const handleChangeSort = (event: CustomEvent) =>
        currentSort$.next(event.detail);

    const handleOpenModal = (
        event: CustomEvent<{
            reviewId: number;
            reviews$: Observable<ReviewEntity[]>;
        }>
    ) => {
        const reviewId = event.detail.reviewId;
        const reviews$ = event.detail.reviews$;
        reviewsGalleryManager.openReviewModal({
            reviews$: reviews$,
            activeReviewId: reviewId,
            widgetName: REVIEWS_WIDGET_NAMES.REVIEWS_PAGE_WIDGET,
        });
    };

    const getAttributeFiltersFields = () => {
        const spaceId = $attributesFilters$.find(
            (a) => a.propertyName === "空間"
        )?.id;
        const sizeId = $attributesFilters$.find(
            (a) => a.propertyName === "空間大小(可選)"
        )?.id;
        const obj: any = {};

        if (spaceId && $openedSpaceTab$ !== "all") {
            obj[`customerFilters[${spaceId}]`] = $openedSpaceTab$;
        }
        if (sizeId && $openedSizeTab$ !== "all") {
            obj[`customerFilters[${sizeId}]`] = $openedSizeTab$;
        }
        return obj;
    };

    const getReviews = (offset = 0) => {
        const filters = serializeFilters(getAttributeFiltersFields());

        const params: GetReviewsWithFiltersParamsDTO = {
            offset,
            perPage: REVIEWS_LIMIT,
            sortingOptions: [$currentSort$],
            onlyProductReviews: true,
            onlyShopReviews: false,
            onlyPinnedReviews: false,
            onlyWithPhotos: true,
            ...filters,
        };
        lastValueFrom(reviewsService.getReviews(params, isMobile));
    };

    onMount(() => {
        spaceTabsManager.openTab("all");
        sizeTabsManager.openTab("all");

        lastValueFrom(wishlistService.getWishlists());
        lastValueFrom(reviewsService.getIllomAttributeFilters());

        const changeSubscription = combineLatest({
            openedSpaceTab$,
            openedSizeTab$,
            currentSort$,
            page$,
        }).subscribe(({ page$ }) => {
            getReviews((page$ - 1) * REVIEWS_LIMIT);
        });

        return () => {
            changeSubscription.unsubscribe();
        };
    });
</script>

<Page>
    <div class="gw-crv-page">
        <div class="gw-crv-page__featured">
            <PageTitle class="gw-crv-page__title">我的 iloom生活</PageTitle>
            <h2 class="gw-crv-page__heading">我與iloom的iloom生活</h2>
            <p class="gw-crv-page__description">
                iloom以溫暖誠摯的目光觀察客人的生活，希望與您一起讓生活空間更加舒適與幸福。
            </p>
            <ReviewsFeaturedList
                {productQuery}
                {reviewsQuery}
                {reviewsService}
                {wishlistsService}
                on:openModal={handleOpenModal}
                {wishlistProductStatusesQuery}
            />
        </div>
        <div class="gw-crv-page__reviews">
            <ReviewsPageSpaceTabs tabsManager={spaceTabsManager} />
            <ReviewsPageSizeTabs tabsManager={sizeTabsManager} />
            <div class="gw-crv-page__divider" />
            <Sort
                tabs={sortTabs}
                on:sort={handleChangeSort}
                currentSort={$currentSort$}
            />
            <ReviewsList
                {productQuery}
                {reviewsQuery}
                {wishlistsService}
                on:openModal={handleOpenModal}
                {wishlistProductStatusesQuery}
            />
        </div>
    </div>

    {#if isMobile}
        <LoadMoreButton
            meta={$meta$}
            loadMoreStatus={$loadMoreStatus$}
            loadMore={getReviews}
        />
    {:else}
        <Pagination meta={$meta$} on:changePage={handleChangePage} />
    {/if}
</Page>

<style lang="postcss">
    :global(.gw-page-title.gw-crv-page__title) {
        margin-bottom: 28px;
    }

    .gw-crv-page {
        margin-bottom: 100px;
        &__featured {
            padding-bottom: 110px;
            background-color: #f2efea;
            box-shadow: 0 0 0 100vmax #f2efea;
            clip-path: inset(0 -100vmax);
        }
        &__heading {
            font-family: "Noto Sans TC";
            font-weight: 400;
            font-size: 22px;
            line-height: 26px;
            color: #2e2e2e;
            text-align: center;
            margin: 0 0 10px;
        }
        &__description {
            font-family: "Noto Sans TC";
            font-weight: 400;
            font-size: 16px;
            line-height: 16px;
            color: #666666;
            text-align: center;
            margin: 0;
        }
        &__reviews {
            padding-top: 60px;
        }
        &__divider {
            border-bottom: 1px dashed #bebebe;
            margin-bottom: 40px;
        }
    }
    @media all and (max-width: 600px) {
        :global(.gw-page-title.gw-crv-page__title) {
            margin-bottom: 12px;
        }

        .gw-crv-page {
            margin-bottom: 50px;
            &__featured {
                padding-bottom: 36px;
            }

            &__heading {
                font-size: 14px;
                line-height: 18px;
                margin: 0 0 14px;
            }
            &__description {
                font-size: 12px;
                line-height: 18px;
            }
            &__reviews {
                padding-top: 0;
            }
            &__divider {
                margin-bottom: 20px;
            }
        }
    }
</style>
